import { parseDateInput } from "@/shared/utils/dateTimeHelpers";

/** Cadence right after the instrument is shown, when the payer is most likely paying. */
export const PAYIN_POLL_FAST_MS = 5_000;
/** Cadence after the first minutes. */
export const PAYIN_POLL_SLOW_MS = 15_000;
const FAST_WINDOW_MS = 5 * 60_000;
/**
 * The window when the screen has no expiry to go by: the longest-lived instrument it shows, a Bre-B dynamic
 * QR, is payable for 60 minutes (coloca-nest `QR_EXPIRY_MINUTES`).
 */
const DEFAULT_WINDOW_MS = 60 * 60_000;
/** Past the expiry: a payment of the last minute, and the sweep that rejects the expired instrument. */
const EXPIRY_GRACE_MS = 5 * 60_000;
/** A CLABE can stay open for days; a screen left open that long stops asking. */
const MAX_WINDOW_MS = 24 * 60 * 60_000;

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The instrument's expiry as epoch ms. A calendar date (SPEI sends `"2026-10-14"`) is payable that whole day. */
function expiryInstant(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const date = parseDateInput(expiresAt);
  if (Number.isNaN(date.getTime())) return null;
  if (CALENDAR_DATE.test(expiresAt)) date.setDate(date.getDate() + 1);
  return date.getTime();
}

/**
 * When the live screen stops asking, as epoch ms: the instrument's expiry plus a grace, so a payment landing
 * in its last minutes still flips the screen even with the socket down. Without an expiry, the 60 minutes of
 * a QR. Never less than the grace, and never more than a day.
 */
export function payinPollDeadline(startedAt: number, expiresAt: string | null | undefined): number {
  const expiry = expiryInstant(expiresAt) ?? startedAt + DEFAULT_WINDOW_MS;
  const end = Math.max(expiry, startedAt) + EXPIRY_GRACE_MS;
  return Math.min(end, startedAt + MAX_WINDOW_MS);
}

/** How long until the next read, or null once the deadline passed (the read that found it was the last). */
export function nextPayinPollDelay(startedAt: number, now: number, deadline: number): number | null {
  if (now >= deadline) return null;
  return now - startedAt < FAST_WINDOW_MS ? PAYIN_POLL_FAST_MS : PAYIN_POLL_SLOW_MS;
}
